import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  ArrowLeft, Coins, Clock, Check, Package, ShoppingBag,
  ArrowRightLeft, Trophy, Sparkles, Star, Gift, Send, X,
  ChevronRight, Lightbulb, Crown, Search, XCircle, Globe
} from 'lucide-react';
import { toast } from 'sonner';

// ── Sound helpers (Web Audio API) ──
const getCtx = () => {
  if (typeof window === 'undefined') return null;
  return new (window.AudioContext || window.webkitAudioContext)();
};
const chime = (ctx, freqs, durs, type = 'sine', vol = 0.2) => {
  if (!ctx) return;
  try {
    if (ctx.state === 'suspended') ctx.resume();
    let t = 0;
    freqs.forEach((f, i) => {
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.type = type; o.frequency.setValueAtTime(f, ctx.currentTime + t);
      g.gain.setValueAtTime(vol, ctx.currentTime + t);
      g.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + t + durs[i]);
      o.connect(g); g.connect(ctx.destination);
      o.start(ctx.currentTime + t); o.stop(ctx.currentTime + t + durs[i]);
      t += durs[i] * 0.55;
    });
  } catch (e) { /* */ }
};

const TYPE_ICONS = { tech: '💻', vehicle: '🚗', learning: '🔬', fun: '🎉', charity: '💝', experience: '🎪' };

const TABS = [
  { id: 'shop', label: 'Shop', icon: <ShoppingBag className="w-4 h-4" /> },
  { id: 'collection', label: 'Collection', icon: <Package className="w-4 h-4" /> },
  { id: 'trade', label: 'Trade', icon: <ArrowRightLeft className="w-4 h-4" /> },
  { id: 'leaderboard', label: 'Ranks', icon: <Trophy className="w-4 h-4" /> },
];

// ── Set Completion Celebration Overlay ──
const SetCelebration = ({ item, rewards, onClose }) => (
  <div data-testid="set-celebration" className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 animate-fade-in">
    <div className="bg-white rounded-3xl p-8 max-w-sm mx-4 text-center animate-pop shadow-2xl border-4 border-amber-400 relative overflow-hidden">
      {[...Array(20)].map((_, i) => (
        <div key={i} className="absolute animate-confetti" style={{
          left: `${Math.random() * 100}%`, animationDelay: `${Math.random() * 0.5}s`,
          backgroundColor: ['#FFD700', '#FF6B6B', '#4ECDC4', '#45B7D1', '#FF9FF3', '#96CEB4'][i % 6],
          width: `${6 + Math.random() * 6}px`, height: `${6 + Math.random() * 6}px`,
          borderRadius: Math.random() > 0.5 ? '50%' : '2px',
        }} />
      ))}
      <div className="relative z-10">
        <div className="text-6xl mb-2">{item?.emoji}</div>
        <div className="flex justify-center gap-1 mb-3">
          {[...Array(3)].map((_, i) => (
            <span key={i} className="text-4xl animate-bounce" style={{ animationDelay: `${i * 0.1}s` }}>⭐</span>
          ))}
        </div>
        <h2 className="text-2xl font-black text-slate-900 font-fredoka mb-1">Set Complete!</h2>
        <p className="text-slate-500 text-sm mb-4">You collected 3 {item?.name}!</p>

        {rewards && (
          <div className="space-y-2 mb-5">
            <div className="bg-amber-50 rounded-xl p-3 flex items-center justify-center gap-2">
              <span className="text-2xl">🪙</span>
              <span className="font-bold text-amber-700">+{rewards.coins} Bonus Coins!</span>
            </div>
            {rewards.food && (
              <div className="bg-purple-50 rounded-xl p-3 flex items-center justify-center gap-2">
                <span className="text-2xl">{rewards.food.emoji}</span>
                <span className="font-bold text-purple-700">+2 {rewards.food.name}!</span>
              </div>
            )}
            {rewards.real_world && (
              <div className="bg-green-50 rounded-xl p-3 border-2 border-green-200">
                <Gift className="w-5 h-5 text-green-600 mx-auto mb-1" />
                <p className="text-green-700 text-sm font-bold">{rewards.real_world}</p>
              </div>
            )}
          </div>
        )}

        <div className="flex justify-center gap-2 text-3xl mb-4">
          <span className="animate-bounce">🐶</span>
          <span className="animate-bounce" style={{ animationDelay: '0.1s' }}>🐱</span>
          <span className="animate-bounce" style={{ animationDelay: '0.2s' }}>🐰</span>
        </div>

        <button
          data-testid="close-celebration"
          onClick={onClose}
          className="px-8 py-3 bg-gradient-to-r from-amber-400 to-yellow-500 text-black rounded-2xl font-bold text-lg shadow-lg hover:shadow-xl transform hover:scale-105 transition-all"
        >Awesome!</button>
      </div>
    </div>
  </div>
);

// ── Redeem Reward Overlay ──
const RedeemOverlay = ({ item, rewards, onClose }) => (
  <div data-testid="redeem-overlay" className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 animate-fade-in">
    <div className="bg-white rounded-3xl p-6 max-w-sm mx-4 text-center animate-pop shadow-2xl">
      <Gift className="w-12 h-12 text-amber-500 mx-auto mb-3" />
      <h2 className="text-xl font-black text-slate-900 font-fredoka mb-1">Rewards Claimed!</h2>
      <p className="text-slate-500 text-sm mb-4">{item?.name} set rewards</p>
      <div className="space-y-2 mb-5">
        <div className="bg-amber-50 rounded-xl p-3 flex items-center justify-center gap-2 text-amber-700 font-bold">
          🪙 +{rewards?.coins} Coins
        </div>
        {rewards?.food && (
          <div className="bg-purple-50 rounded-xl p-3 flex items-center justify-center gap-2 text-purple-700 font-bold">
            {rewards.food.emoji} +2 {rewards.food.name}
          </div>
        )}
        {rewards?.real_world && (
          <div className="bg-green-50 rounded-xl p-3 border-2 border-green-200 text-green-700 text-sm font-bold">
            <Gift className="w-4 h-4 inline mr-1" />{rewards.real_world}
          </div>
        )}
      </div>
      <button onClick={onClose} className="px-6 py-3 bg-primary text-black rounded-2xl font-bold shadow-md hover:shadow-lg transition-all">
        Got it!
      </button>
    </div>
  </div>
);

const MarketPage = () => {
  const { user, api, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [market, setMarket] = useState(null);
  const [collection, setCollection] = useState(null);
  const [trades, setTrades] = useState(null);
  const [leaderboard, setLeaderboard] = useState(null);
  const [suggestions, setSuggestions] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('shop');
  const [selectedItem, setSelectedItem] = useState(null);
  const [purchasing, setPurchasing] = useState(false);
  const [celebration, setCelebration] = useState(null);
  const [redeemResult, setRedeemResult] = useState(null);
  const [tradeUsername, setTradeUsername] = useState('');
  const [tradeItem, setTradeItem] = useState(null);
  const [wantDescription, setWantDescription] = useState('');
  const [tradeFeed, setTradeFeed] = useState(null);
  const [feedSearch, setFeedSearch] = useState('');
  const [milestonePopup, setMilestonePopup] = useState(null);
  const audioCtx = useRef(null);

  useEffect(() => { audioCtx.current = getCtx(); return () => { audioCtx.current?.close().catch(() => {}); }; }, []);

  const apiRef = useRef(api);
  useEffect(() => { apiRef.current = api; }, [api]);

  const play = useCallback((type) => {
    const s = {
      buy: () => chime(audioCtx.current, [523, 659, 784], [0.12, 0.12, 0.2], 'sine', 0.18),
      set: () => chime(audioCtx.current, [523, 659, 784, 1047, 1319], [0.12, 0.12, 0.12, 0.12, 0.3], 'sine', 0.22),
      coins: () => chime(audioCtx.current, [1200, 1400, 1600], [0.05, 0.05, 0.1], 'sine', 0.12),
      click: () => chime(audioCtx.current, [880], [0.06], 'sine', 0.1),
    };
    s[type]?.();
  }, []);

  const fetchMarket = useCallback(async () => {
    try {
      const res = await apiRef.current.get('/market/collectibles');
      setMarket(res.data);
    } catch (err) { toast.error('Could not load market'); }
  }, []);

  const fetchCollection = useCallback(async () => {
    try { const res = await apiRef.current.get('/market/collection'); setCollection(res.data); } catch (e) { /* */ }
  }, []);

  const fetchTrades = useCallback(async () => {
    try { const res = await apiRef.current.get('/market/trades'); setTrades(res.data); } catch (e) { /* */ }
  }, []);

  const fetchLeaderboard = useCallback(async () => {
    try { const res = await apiRef.current.get('/market/leaderboard'); setLeaderboard(res.data); } catch (e) { /* */ }
  }, []);

  const fetchSuggestions = useCallback(async () => {
    try { const res = await apiRef.current.get('/market/suggestions'); setSuggestions(res.data); } catch (e) { /* */ }
  }, []);

  const fetchTradeFeed = useCallback(async (query = '') => {
    try {
      const res = await apiRef.current.get(`/market/trade/feed?search=${encodeURIComponent(query)}`);
      setTradeFeed(res.data);
    } catch (e) { /* */ }
  }, []);

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      await fetchMarket();
      setLoading(false);
    };
    init();
  }, [fetchMarket]);

  useEffect(() => {
    if (activeTab === 'collection') { fetchCollection(); fetchSuggestions(); }
    if (activeTab === 'trade') { fetchTrades(); fetchCollection(); fetchTradeFeed(); }
    if (activeTab === 'leaderboard') fetchLeaderboard();
  }, [activeTab, fetchCollection, fetchTrades, fetchLeaderboard, fetchSuggestions, fetchTradeFeed]);

  const handleBuy = async (item) => {
    setPurchasing(true);
    try {
      const res = await apiRef.current.post('/market/collectibles/buy', { item_id: item.id });
      play('buy');
      toast.success(res.data.message);

      if (res.data.set_completed) {
        play('set');
        // Auto-redeem to show celebration
        try {
          const redeemRes = await apiRef.current.post('/market/redeem', { item_id: item.id });
          setCelebration({ item, rewards: redeemRes.data.rewards });
          if (redeemRes.data.milestone_bonus) {
            setTimeout(() => setMilestonePopup(redeemRes.data.milestone_bonus), 2000);
          }
        } catch (e) {
          setCelebration({ item, rewards: null });
        }
      }

      await refreshUser();
      await fetchMarket();
      setSelectedItem(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not buy item!');
    } finally {
      setPurchasing(false);
    }
  };

  const handleRedeem = async (itemId) => {
    try {
      const item = collection?.collection?.find(c => c.id === itemId);
      const res = await apiRef.current.post('/market/redeem', { item_id: itemId });
      play('coins');
      setRedeemResult({ item, rewards: res.data.rewards });
      if (res.data.milestone_bonus) {
        setTimeout(() => {
          setMilestonePopup(res.data.milestone_bonus);
        }, 1500);
      }
      await refreshUser();
      await fetchCollection();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not redeem set!');
    }
  };

  const handleSendTrade = async (offerUid, toUsername) => {
    try {
      const res = await apiRef.current.post('/market/trade/send', {
        offer_item_uid: offerUid,
        to_username: toUsername || undefined,
        want_description: wantDescription || undefined,
      });
      play('click');
      toast.success(res.data.message);
      setTradeItem(null);
      setTradeUsername('');
      setWantDescription('');
      await fetchTrades();
      await fetchTradeFeed();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Trade failed!');
    }
  };

  const handleCancelTrade = async (tradeId) => {
    try {
      const res = await apiRef.current.post('/market/trade/cancel', { trade_id: tradeId });
      play('click');
      toast.success(res.data.message);
      await fetchTrades();
      await fetchTradeFeed();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Cancel failed!');
    }
  };

  const handleRespondTrade = async (tradeId, action) => {
    try {
      const res = await apiRef.current.post('/market/trade/respond', { trade_id: tradeId, action });
      play(action === 'accept' ? 'buy' : 'click');
      toast.success(res.data.message);
      await fetchTrades();
      await fetchCollection();
      await refreshUser();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed!');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-6xl animate-bounce mb-4">🏪</div>
          <p className="text-xl text-slate-600 font-fredoka">Opening the market...</p>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="market-page" className="min-h-screen bg-slate-50">
      {celebration && <SetCelebration item={celebration.item} rewards={celebration.rewards} onClose={() => setCelebration(null)} />}
      {redeemResult && <RedeemOverlay item={redeemResult.item} rewards={redeemResult.rewards} onClose={() => setRedeemResult(null)} />}
      {milestonePopup && (
        <div data-testid="milestone-popup" className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 animate-fade-in">
          {/* Coin rain animation */}
          {[...Array(12)].map((_, i) => (
            <span key={i} className="animate-coin-rain" style={{
              left: `${10 + Math.random() * 80}%`, top: `${Math.random() * 30}%`,
              animationDelay: `${Math.random() * 0.8}s`, position: 'fixed', zIndex: 51
            }}>🪙</span>
          ))}
          <div className="bg-white rounded-3xl p-6 max-w-sm mx-4 text-center animate-pop shadow-2xl border-4 border-green-400 relative z-52">
            <div className="text-5xl mb-3">🏆</div>
            <h2 className="text-xl font-black text-slate-900 font-fredoka mb-2">Milestone Bonus!</h2>
            <div className="space-y-2 mb-4">
              {milestonePopup.awarded.map(m => (
                <div key={m.milestone} className="bg-green-50 rounded-xl p-3 flex items-center justify-center gap-2">
                  <span className="text-xl">🪙</span>
                  <span className="font-bold text-green-700">
                    {m.milestone} set{m.milestone > 1 ? 's' : ''} complete: +{m.coins} coins!
                  </span>
                </div>
              ))}
            </div>
            <p className="text-slate-500 text-sm mb-4">Keep collecting to unlock more rewards!</p>
            <button onClick={() => { setMilestonePopup(null); refreshUser(); }}
              className="px-6 py-3 bg-gradient-to-r from-green-400 to-emerald-500 text-white rounded-2xl font-bold shadow-md hover:shadow-lg transition-all"
              data-testid="close-milestone-btn"
            >Awesome!</button>
          </div>
        </div>
      )}

      {/* Header */}
      <header className="bg-white border-b-2 border-slate-100 sticky top-0 z-40">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button data-testid="back-btn" onClick={() => navigate('/coin-world')} className="p-2 hover:bg-slate-100 rounded-full transition-colors">
              <ArrowLeft className="w-5 h-5 text-slate-600" />
            </button>
            <h1 className="text-lg font-bold text-slate-900 font-fredoka">Mini Market</h1>
          </div>
          <div className="flex items-center gap-1 px-3 py-1 bg-primary/20 rounded-full">
            <Coins className="w-4 h-4 text-primary" />
            <span data-testid="coin-balance" className="font-bold text-sm text-slate-900">{market?.coins || 0}</span>
          </div>
        </div>
      </header>

      {/* Tabs */}
      <div className="max-w-3xl mx-auto px-4 pt-4">
        <div className="flex gap-1 bg-slate-100 rounded-2xl p-1">
          {TABS.map(tab => (
            <button
              key={tab.id}
              data-testid={`tab-${tab.id}`}
              onClick={() => setActiveTab(tab.id)}
              className={`flex-1 py-2.5 rounded-xl font-bold text-sm flex items-center justify-center gap-1.5 transition-all relative ${
                activeTab === tab.id ? 'bg-white text-slate-900 shadow-md' : 'text-slate-500 hover:text-slate-700'
              }`}
            >
              {tab.icon}
              <span className="hidden sm:inline">{tab.label}</span>
              {tab.id === 'trade' && (trades?.incoming?.length || 0) > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">
                  {trades.incoming.length}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      <main className="max-w-3xl mx-auto px-4 py-5 space-y-5">

        {/* ═══ SHOP TAB ═══ */}
        {activeTab === 'shop' && (
          <>
            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-4 flex items-center gap-4 border border-blue-100">
              <span className="text-4xl">🏪</span>
              <div>
                <h2 className="text-lg font-bold text-slate-900 font-fredoka">Today's Items</h2>
                <p className="text-slate-600 text-sm">Collect 3 of the same item to complete a set and win rewards!</p>
              </div>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {market?.items?.map(item => {
                const canBuy = item.can_afford && !item.max_reached && !item.purchased_today;
                return (
                  <div key={item.id} data-testid={`market-item-${item.id}`}
                    className={`bg-white rounded-2xl border-2 overflow-hidden transition-all ${item.max_reached ? 'border-green-400' : item.purchased_today ? 'border-blue-300' : canBuy ? 'border-slate-200 hover:border-primary hover:shadow-md' : 'border-slate-200 opacity-60'}`}
                  >
                    {item.rarity === 'rare' && (
                      <div className="bg-gradient-to-r from-amber-400 to-yellow-400 text-center py-0.5">
                        <span className="text-[10px] font-bold text-amber-900">Rare Collectible</span>
                      </div>
                    )}
                    <div className="p-4 text-center">
                      <div className="text-4xl mb-2">{item.emoji}</div>
                      <h3 className="font-bold text-slate-900 text-sm">{item.name}</h3>
                      <p className="text-[11px] text-slate-500 mt-1 line-clamp-1">{item.description}</p>

                      {/* Set progress dots */}
                      <div className="flex items-center justify-center gap-1.5 my-2">
                        {[0, 1, 2].map(i => (
                          <div key={i} className={`w-4 h-4 rounded-full border-2 transition-all ${
                            i < item.owned_count ? 'bg-primary border-primary scale-110' : 'border-slate-300 bg-slate-100'
                          }`} />
                        ))}
                        <span className="text-[10px] text-slate-400 ml-1">{item.owned_count}/3</span>
                      </div>

                      <button
                        data-testid={`buy-${item.id}`}
                        onClick={() => !item.max_reached && canBuy && setSelectedItem(item)}
                        disabled={!canBuy}
                        className={`w-full py-2.5 rounded-xl font-bold text-sm flex items-center justify-center gap-1.5 transition-all ${
                          item.max_reached ? 'bg-green-100 text-green-700' : item.purchased_today ? 'bg-blue-100 text-blue-600' : canBuy ? 'bg-primary text-black active:scale-95' : 'bg-slate-200 text-slate-400'
                        }`}
                      >
                        {item.max_reached ? (
                          <><Check className="w-4 h-4" /> Set Complete!</>
                        ) : item.purchased_today ? (
                          <><Check className="w-4 h-4" /> Bought Today</>
                        ) : !item.can_afford ? (
                          <><Coins className="w-4 h-4" /> {item.cost}c</>
                        ) : (
                          <><Coins className="w-4 h-4" /> Buy {item.cost}c</>
                        )}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="text-center text-slate-400 text-sm flex items-center justify-center gap-2">
              <Clock className="w-4 h-4" /> New items arrive {market?.refresh_in || 'tomorrow'}!
            </div>
          </>
        )}

        {/* ═══ COLLECTION TAB ═══ */}
        {activeTab === 'collection' && (
          <>
            {/* Stats */}
            <div className="grid grid-cols-3 gap-3">
              <div className="bg-white rounded-2xl p-3 text-center border border-slate-200">
                <p className="text-2xl font-black text-slate-900">{collection?.total_items || 0}</p>
                <p className="text-[11px] text-slate-500 font-bold">Total Items</p>
              </div>
              <div className="bg-white rounded-2xl p-3 text-center border border-slate-200">
                <p className="text-2xl font-black text-amber-600">{collection?.total_sets || 0}</p>
                <p className="text-[11px] text-slate-500 font-bold">Sets Done</p>
              </div>
              <div className="bg-white rounded-2xl p-3 text-center border border-slate-200">
                <p className="text-2xl font-black text-purple-600">{collection?.collection?.length || 0}</p>
                <p className="text-[11px] text-slate-500 font-bold">Unique Items</p>
              </div>
            </div>

            {/* Set Milestone Rewards */}
            <div data-testid="milestone-tracker" className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-2xl p-4 border border-green-200">
              <h3 className="font-bold text-slate-800 text-sm mb-2 flex items-center gap-2">
                <Trophy className="w-4 h-4 text-green-600" /> Set Milestone Rewards
              </h3>
              <div className="space-y-2">
                {[
                  { sets: 1, coins: 25 },
                  { sets: 2, coins: 60 },
                  { sets: 3, coins: 100 },
                ].map(m => {
                  const claimed = collection?.milestones_claimed?.includes(m.sets);
                  const reached = (collection?.total_sets || 0) >= m.sets;
                  return (
                    <div key={m.sets} data-testid={`milestone-${m.sets}`} className={`flex items-center gap-3 p-2 rounded-lg ${claimed ? 'bg-green-100' : reached ? 'bg-amber-50' : 'bg-white/60'}`}>
                      <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${claimed ? 'bg-green-500 text-white' : reached ? 'bg-amber-400 text-white' : 'bg-slate-200 text-slate-500'}`}>
                        {claimed ? <Check className="w-4 h-4" /> : m.sets}
                      </div>
                      <div className="flex-1">
                        <p className="text-sm font-bold text-slate-700">{m.sets} Set{m.sets > 1 ? 's' : ''} Complete</p>
                      </div>
                      <span className={`text-sm font-bold ${claimed ? 'text-green-600' : 'text-slate-400'}`}>
                        +{m.coins} 🪙
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Suggestions */}
            {suggestions?.needs?.length > 0 && (
              <div className="bg-amber-50 rounded-2xl p-4 border border-amber-200">
                <div className="flex items-center gap-2 mb-2">
                  <Lightbulb className="w-4 h-4 text-amber-600" />
                  <p className="font-bold text-amber-800 text-sm">Almost There!</p>
                </div>
                <div className="space-y-1.5">
                  {suggestions.needs.slice(0, 3).map(n => (
                    <div key={n.item_id} className="flex items-center gap-2 text-sm text-amber-700">
                      <span>{n.emoji}</span>
                      <span>{n.have}/3 {n.name} — need {n.need} more!</span>
                    </div>
                  ))}
                </div>
                {suggestions.trade_tips?.length > 0 && (
                  <div className="mt-2 pt-2 border-t border-amber-200">
                    {suggestions.trade_tips.slice(0, 2).map((tip, i) => (
                      <p key={i} className="text-xs text-amber-600 flex items-center gap-1">
                        <ArrowRightLeft className="w-3 h-3" /> {tip.message}
                      </p>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Items */}
            {collection?.collection?.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {collection.collection.map(item => (
                  <div key={item.id} data-testid={`collection-item-${item.id}`}
                    className={`bg-white rounded-2xl border-2 p-4 text-center transition-all ${
                      item.set_complete ? 'border-amber-400 bg-amber-50/50' : 'border-slate-200'
                    }`}
                  >
                    <div className="text-4xl mb-1">{item.emoji}</div>
                    <h4 className="font-bold text-slate-800 text-sm">{item.name}</h4>

                    {/* Progress circles */}
                    <div className="flex justify-center gap-1.5 my-2">
                      {[0, 1, 2].map(i => (
                        <div key={i} className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all ${
                          i < item.count ? 'bg-primary border-primary' : 'border-slate-300'
                        }`}>
                          {i < item.count && <Check className="w-3 h-3 text-black" />}
                        </div>
                      ))}
                    </div>
                    <p className="text-xs text-slate-500">{item.count}/3 collected</p>

                    {item.set_complete && !item.set_redeemed && (
                      <button
                        data-testid={`redeem-${item.id}`}
                        onClick={() => handleRedeem(item.id)}
                        className="mt-2 w-full py-2 bg-gradient-to-r from-amber-400 to-yellow-500 text-black rounded-xl font-bold text-xs flex items-center justify-center gap-1 hover:shadow-md transition-all"
                      >
                        <Gift className="w-3 h-3" /> Claim Reward
                      </button>
                    )}
                    {item.set_redeemed && (
                      <div className="mt-2 py-1.5 bg-green-100 rounded-xl text-green-700 text-xs font-bold flex items-center justify-center gap-1">
                        <Check className="w-3 h-3" /> Redeemed
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12 bg-white/60 rounded-3xl">
                <span className="text-5xl block mb-3">📦</span>
                <p className="text-slate-500">No items yet! Visit the Shop to start collecting.</p>
              </div>
            )}
          </>
        )}

        {/* ═══ TRADE TAB ═══ */}
        {activeTab === 'trade' && (
          <>
            {/* Trade limits info */}
            <div className="bg-white rounded-2xl p-4 border border-slate-200 text-sm">
              <div className="flex items-center justify-between">
                <span className="text-slate-600">Trades left today:</span>
                <span className="font-bold text-slate-900">{trades?.trades_remaining_today ?? 2}/2</span>
              </div>
              <div className="flex items-center justify-between mt-1">
                <span className="text-slate-600">Rare trades left:</span>
                <span className="font-bold text-amber-600">{trades?.rare_trades_remaining_today ?? 1}/1</span>
              </div>
            </div>

            {/* Send Trade */}
            <div className="bg-white rounded-2xl p-4 border border-slate-200">
              <h3 className="font-bold text-slate-800 text-sm mb-3 flex items-center gap-2">
                <Send className="w-4 h-4" /> Send a Trade
              </h3>

              {tradeItem ? (
                <div className="space-y-3">
                  <div className="flex items-center gap-3 bg-purple-50 rounded-xl p-3">
                    <span className="text-3xl">{tradeItem.emoji}</span>
                    <div className="flex-1">
                      <p className="font-bold text-slate-800 text-sm">{tradeItem.name}</p>
                      <p className="text-[10px] text-slate-500">ID: {tradeItem.uid}</p>
                    </div>
                    <button onClick={() => { setTradeItem(null); setWantDescription(''); }} className="p-1 hover:bg-purple-100 rounded-full">
                      <X className="w-4 h-4 text-slate-400" />
                    </button>
                  </div>

                  <input
                    data-testid="trade-username-input"
                    value={tradeUsername}
                    onChange={e => setTradeUsername(e.target.value)}
                    placeholder="Friend's username (leave empty for random)"
                    className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl text-sm focus:outline-none focus:border-purple-400"
                  />

                  <input
                    data-testid="trade-want-input"
                    value={wantDescription}
                    onChange={e => setWantDescription(e.target.value)}
                    placeholder="What do you want in return? (e.g. Gaming Laptop)"
                    className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl text-sm focus:outline-none focus:border-indigo-400"
                  />

                  <div className="flex gap-2">
                    <button
                      data-testid="send-trade-btn"
                      onClick={() => handleSendTrade(tradeItem.uid, tradeUsername)}
                      className="flex-1 py-3 bg-gradient-to-r from-purple-500 to-indigo-600 text-white rounded-xl font-bold text-sm flex items-center justify-center gap-2 hover:shadow-md transition-all"
                    >
                      <Send className="w-4 h-4" /> {tradeUsername ? 'Send to Friend' : 'Send to Random'}
                    </button>
                  </div>
                </div>
              ) : (
                <div>
                  <p className="text-slate-500 text-sm mb-3">Pick an item from your collection to offer:</p>
                  <div className="grid grid-cols-4 gap-2 max-h-48 overflow-y-auto">
                    {collection?.collection?.map(item =>
                      item.uids.map(uid => (
                        <button
                          key={uid}
                          data-testid={`trade-select-${uid}`}
                          onClick={() => { play('click'); setTradeItem({ ...item, uid }); }}
                          className="p-2 bg-slate-50 rounded-xl border border-slate-200 hover:border-purple-300 text-center transition-all"
                        >
                          <span className="text-2xl block">{item.emoji}</span>
                          <span className="text-[9px] text-slate-500">{uid.slice(0, 4)}</span>
                        </button>
                      ))
                    )}
                  </div>
                  {(!collection?.collection || collection.collection.length === 0) && (
                    <p className="text-slate-400 text-sm text-center py-4">No items to trade yet!</p>
                  )}
                </div>
              )}
            </div>

            {/* Incoming Trades */}
            {trades?.incoming?.length > 0 && (
              <div>
                <h3 className="font-bold text-slate-800 text-sm mb-2 flex items-center gap-2">
                  <Package className="w-4 h-4" /> Incoming Trades ({trades.incoming.length})
                </h3>
                <div className="space-y-2">
                  {trades.incoming.map(t => (
                    <div key={t.trade_id} data-testid={`incoming-trade-${t.trade_id}`}
                      className="bg-white rounded-2xl p-4 border border-slate-200 flex items-center gap-3"
                    >
                      <span className="text-3xl">{t.offer_item_emoji}</span>
                      <div className="flex-1">
                        <p className="font-bold text-slate-800 text-sm">{t.from_username} offers {t.offer_item_name}</p>
                        <p className="text-[10px] text-slate-500">
                          {t.offer_item_rarity === 'rare' ? '✨ Rare item' : 'Common item'}
                        </p>
                      </div>
                      <div className="flex gap-1.5">
                        <button data-testid={`accept-${t.trade_id}`} onClick={() => handleRespondTrade(t.trade_id, 'accept')}
                          className="px-3 py-2 bg-green-500 text-white rounded-xl text-xs font-bold">Accept</button>
                        <button data-testid={`decline-${t.trade_id}`} onClick={() => handleRespondTrade(t.trade_id, 'decline')}
                          className="px-3 py-2 bg-slate-200 text-slate-600 rounded-xl text-xs font-bold">No</button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Outgoing Trades */}
            {trades?.outgoing?.length > 0 && (
              <div>
                <h3 className="font-bold text-slate-800 text-sm mb-2">Pending Sent Trades</h3>
                <div className="space-y-2">
                  {trades.outgoing.map(t => (
                    <div key={t.trade_id} data-testid={`outgoing-trade-${t.trade_id}`} className="bg-slate-50 rounded-xl p-3 flex items-center gap-3 text-sm">
                      <span className="text-2xl">{t.offer_item_emoji}</span>
                      <div className="flex-1">
                        <p className="text-slate-700">Offering {t.offer_item_name} to <strong>{t.to_username}</strong></p>
                        {t.want_description && (
                          <p className="text-[10px] text-indigo-600 mt-0.5">Wants: {t.want_description}</p>
                        )}
                      </div>
                      <button
                        data-testid={`cancel-trade-${t.trade_id}`}
                        onClick={() => handleCancelTrade(t.trade_id)}
                        className="px-3 py-1.5 bg-red-100 text-red-600 rounded-lg text-xs font-bold hover:bg-red-200 transition-colors flex items-center gap-1"
                      >
                        <XCircle className="w-3 h-3" /> Cancel
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Global Trade Feed */}
            <div>
              <h3 className="font-bold text-slate-800 text-sm mb-2 flex items-center gap-2">
                <Globe className="w-4 h-4" /> Global Trade Feed
              </h3>
              <div className="relative mb-3">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input
                  data-testid="trade-feed-search"
                  value={feedSearch}
                  onChange={e => {
                    setFeedSearch(e.target.value);
                    fetchTradeFeed(e.target.value);
                  }}
                  placeholder="Search for an item you want..."
                  className="w-full pl-10 pr-4 py-3 border-2 border-slate-200 rounded-xl text-sm focus:outline-none focus:border-indigo-400"
                />
              </div>
              {tradeFeed?.trades?.length > 0 ? (
                <div className="space-y-2 max-h-72 overflow-y-auto">
                  {tradeFeed.trades.map(t => (
                    <div key={t.trade_id} data-testid={`feed-trade-${t.trade_id}`}
                      className="bg-white rounded-xl p-3 border border-slate-200 flex items-center gap-3 text-sm"
                    >
                      <span className="text-2xl">{t.offer_item_emoji}</span>
                      <div className="flex-1 min-w-0">
                        <p className="font-bold text-slate-800 truncate">{t.from_username} offers {t.offer_item_name}</p>
                        {t.want_description && (
                          <p className="text-[10px] text-indigo-600 truncate">Wants: {t.want_description}</p>
                        )}
                        <p className="text-[10px] text-slate-400">
                          {t.offer_item_rarity === 'rare' && <span className="text-amber-600 font-bold mr-1">Rare</span>}
                          to {t.to_username}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-6 bg-slate-50 rounded-xl">
                  <span className="text-3xl block mb-2">🔍</span>
                  <p className="text-slate-400 text-sm">{feedSearch ? 'No trades found for that item' : 'No active trades right now'}</p>
                </div>
              )}
            </div>

            {/* Trading Rules */}
            <div className="bg-slate-50 rounded-2xl p-4 border border-slate-200 text-xs text-slate-500 space-y-1">
              <p className="font-bold text-slate-700 text-sm mb-1">Trading Rules</p>
              <p>- You can make <strong>2 trades per day</strong></p>
              <p>- Only <strong>1 rare item trade</strong> per day</p>
              <p>- When accepting, you give a random item from your collection</p>
              <p>- All trades are fair and child-safe</p>
            </div>
          </>
        )}

        {/* ═══ LEADERBOARD TAB ═══ */}
        {activeTab === 'leaderboard' && (
          <>
            <div className="bg-gradient-to-br from-amber-50 to-yellow-50 rounded-2xl p-4 border border-amber-200 text-center">
              <Crown className="w-8 h-8 text-amber-500 mx-auto mb-1" />
              <h3 className="font-bold text-slate-900 font-fredoka">Top Collectors</h3>
              <p className="text-slate-500 text-sm">Who has the best collection?</p>
            </div>

            <div className="space-y-2">
              {leaderboard?.leaderboard?.length > 0 ? (
                leaderboard.leaderboard.map((entry, idx) => (
                  <div key={idx} data-testid={`lb-rank-${entry.rank}`}
                    className={`bg-white rounded-2xl p-4 flex items-center gap-3 border-2 transition-all ${
                      entry.is_me ? 'border-primary bg-primary/5' : 'border-slate-200'
                    } ${entry.rank <= 3 ? 'shadow-sm' : ''}`}
                  >
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center font-black text-lg ${
                      entry.rank === 1 ? 'bg-amber-400 text-white' : entry.rank === 2 ? 'bg-slate-300 text-white' : entry.rank === 3 ? 'bg-amber-700 text-white' : 'bg-slate-100 text-slate-500'
                    }`}>
                      {entry.rank <= 3 ? ['🥇', '🥈', '🥉'][entry.rank - 1] : entry.rank}
                    </div>
                    <div className="flex-1">
                      <p className="font-bold text-slate-800 text-sm">
                        {entry.username} {entry.is_me && <span className="text-primary text-xs">(You)</span>}
                      </p>
                      <p className="text-[11px] text-slate-500">
                        {entry.total_items} items · {entry.unique_items} unique · {entry.sets_completed} sets
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="font-black text-slate-900">{entry.score}</p>
                      <p className="text-[10px] text-slate-400">points</p>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-12 bg-white/60 rounded-3xl">
                  <span className="text-5xl block mb-3">🏆</span>
                  <p className="text-slate-500">No collectors yet! Be the first to start collecting.</p>
                </div>
              )}
            </div>
          </>
        )}
      </main>

      {/* Purchase Modal */}
      {selectedItem && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-3xl p-6 w-full max-w-sm animate-pop">
            <div className="text-center mb-4">
              <div className="text-5xl mb-2">{selectedItem.emoji}</div>
              <h3 className="font-bold text-lg text-slate-900">{selectedItem.name}</h3>
              <p className="text-slate-500 text-sm">{selectedItem.description}</p>
              {selectedItem.rarity === 'rare' && (
                <span className="inline-block mt-1 text-[10px] bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full font-bold">Rare</span>
              )}
            </div>

            {/* Set progress */}
            <div className="flex items-center justify-center gap-2 mb-4">
              {[0, 1, 2].map(i => (
                <div key={i} className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                  i < selectedItem.owned_count ? 'bg-primary border-primary' : i === selectedItem.owned_count ? 'border-primary bg-primary/20 animate-pulse' : 'border-slate-300'
                }`}>
                  {i < selectedItem.owned_count && <Check className="w-3 h-3 text-black" />}
                  {i === selectedItem.owned_count && <span className="text-xs">+</span>}
                </div>
              ))}
              <span className="text-xs text-slate-500 ml-1">{selectedItem.owned_count + 1}/3 after buying</span>
            </div>

            {selectedItem.owned_count === 2 && (
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 mb-4 text-center">
                <Sparkles className="w-4 h-4 text-amber-500 inline mr-1" />
                <span className="text-sm font-bold text-amber-700">This will complete the set! Rewards await!</span>
              </div>
            )}

            <div className="bg-blue-50 rounded-2xl p-3 mb-4 text-sm">
              <div className="flex justify-between"><span className="text-slate-600">Price:</span><span className="font-bold flex items-center gap-1"><Coins className="w-4 h-4 text-primary" />{selectedItem.cost}c</span></div>
              <div className="flex justify-between mt-1"><span className="text-slate-600">After:</span><span className="font-bold">{(market?.coins || 0) - selectedItem.cost}c left</span></div>
            </div>

            <div className="flex gap-3">
              <button onClick={() => setSelectedItem(null)} disabled={purchasing} className="flex-1 py-3 bg-slate-100 rounded-2xl font-bold text-slate-700">Cancel</button>
              <button data-testid="confirm-purchase-btn" onClick={() => handleBuy(selectedItem)} disabled={purchasing}
                className="flex-1 py-3 bg-primary rounded-2xl font-bold text-black">
                {purchasing ? 'Buying...' : 'Buy!'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MarketPage;
